import React, { useEffect, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { Accordion, Button, Message, Modal, Table } from 'semantic-ui-react';
import { CheckCircle2, RotateCcw } from 'lucide-react';
import { formatDateWithPattern } from '../../utils/time/formatDateWithPattern';
import { getAccountAudit, getFlagAudit } from './retentionApi';
import { AuditEntry, RetentionAudit, auditAction } from './types';
import { i18nKeys } from './constants';
import styles from './DeidentifyModal.module.css';

const i18nRoot = i18nKeys.DEIDENTIFY_AUDIT_ROOT;

// The same pattern the grid's DateTimeFormatter uses, so a timestamp reads identically here and in
// the retention grid's own firstFlaggedAt and anonymisedAt columns.
const DATETIME_PATTERN = 'YYYY/MM/DD h:mm A';

interface DeidentifyAuditModalProps {
  /** One of the two is required: the grid has a flag row, the account page may not. */
  accountId?: number;
  flagId?: number;
  onClose: () => void;
}

const actionIcon = (action: AuditEntry['action']) =>
  action === auditAction.reverted ? (
    <RotateCcw
      size={16}
      color="var(--color-text-secondary, #757575)"
      aria-hidden
    />
  ) : (
    <CheckCircle2 size={16} color="var(--color-success, #2e7d32)" aria-hidden />
  );

/**
 * The history of what was de-identified for one subject, read from the permanent audit trail.
 *
 * Deliberately shows column names without values: the values live only in the reversal snapshot and
 * are deleted when it is purged, which is what makes a de-identification irreversible. This screen
 * has to keep working long after that, so it never had them to show.
 */
const DeidentifyAuditModal = ({
  accountId,
  flagId,
  onClose
}: DeidentifyAuditModalProps) => {
  const [audit, setAudit] = useState<RetentionAudit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(0);

  useEffect(() => {
    let cancelled = false;
    // Both callers are untyped .jsx, so the "one of the two is required" contract cannot be enforced
    // at compile time. Without the last branch the account lookup would request
    // account/undefined/audit. Written as a chain rather than an early return so each id narrows.
    let load: Promise<RetentionAudit>;
    if (flagId != null) {
      load = getFlagAudit(flagId);
    } else if (accountId != null) {
      load = getAccountAudit(accountId);
    } else {
      setError(I18n.t(`${i18nRoot}.error`));
      return undefined;
    }

    load
      .then((result) => {
        if (!cancelled) {
          setAudit(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(I18n.t(`${i18nRoot}.error`));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, flagId]);

  const entries: AuditEntry[] = audit?.entries || [];

  return (
    <Modal open onClose={onClose} size="small" closeIcon>
      <Modal.Header>{I18n.t(`${i18nRoot}.title`)}</Modal.Header>
      <Modal.Content>
        <div aria-live="polite">
          <p>{I18n.t(`${i18nRoot}.intro`)}</p>

          {error && (
            <Message negative size="small">
              {error}
            </Message>
          )}

          {!audit && !error && <p>{I18n.t(`${i18nRoot}.loading`)}</p>}

          {audit && entries.length === 0 && (
            <Message info size="small">
              {I18n.t(`${i18nRoot}.empty`)}
            </Message>
          )}

          {entries.length > 0 && (
            <Accordion styled fluid>
              {entries.map((entry, index) => {
                const isOpen = expanded === index;
                return (
                  <React.Fragment key={`${entry.performedAt}-${entry.action}`}>
                    <Accordion.Title
                      active={isOpen}
                      index={index}
                      onClick={() => setExpanded(isOpen ? null : index)}
                      aria-expanded={isOpen}
                    >
                      <span className={styles.title}>
                        {actionIcon(entry.action)}
                        <span className={styles.titleEmail}>
                          {I18n.t(`${i18nRoot}.action.${entry.action}`)}{' '}
                          {formatDateWithPattern(
                            entry.performedAt,
                            DATETIME_PATTERN,
                            entry.performedAt
                          )}
                        </span>
                        <span className={styles.titleOutcome}>
                          {entry.performedBy
                            ? I18n.t(`${i18nRoot}.by`, {
                                name: entry.performedBy
                              })
                            : I18n.t(`${i18nRoot}.bySystem`)}
                        </span>
                      </span>
                    </Accordion.Title>
                    <Accordion.Content active={isOpen}>
                      {(entry.tables || []).map((table) => (
                        <div key={table.name} className={styles.detail}>
                          <strong>{table.name}</strong>{' '}
                          <span>{table.rows}</span>
                          {table.columns && table.columns.length > 0 && (
                            <Table
                              basic="very"
                              compact
                              size="small"
                              unstackable
                            >
                              <Table.Body>
                                <Table.Row>
                                  <Table.Cell className={styles.columnName}>
                                    {I18n.t(`${i18nRoot}.columns`)}
                                  </Table.Cell>
                                  <Table.Cell className={styles.columnValue}>
                                    {table.columns.join(', ')}
                                  </Table.Cell>
                                </Table.Row>
                              </Table.Body>
                            </Table>
                          )}
                        </div>
                      ))}
                    </Accordion.Content>
                  </React.Fragment>
                );
              })}
            </Accordion>
          )}
        </div>

        <div
          className="button-wrapper align-right"
          style={{ marginTop: '16px' }}
        >
          <Button primary onClick={onClose}>
            {I18n.t(`${i18nRoot}.close`)}
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default DeidentifyAuditModal;
